import type { CSSProperties, ReactNode } from "react"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

const mail = {
	canvas: "#F7F7F8",
	card: "#FFFFFF",
	border: "#ECECEE",
	radius: "18px",
	heading: "#282a30",
	body: "#3C4149",
	secondary: "#6F6E77",
	muted: "#86848d",
	accent: "#4CB782",
	font: "Inter, ui-sans-serif, sans-serif",
	width: 584,
	padding: 32,
}

const bodyText: CSSProperties = {
	fontSize: "15px",
	lineHeight: "23px",
	color: mail.body,
	margin: 0,
}

const secondaryText: CSSProperties = {
	fontSize: "14px",
	lineHeight: "21px",
	color: mail.secondary,
	margin: 0,
}

const legalText: CSSProperties = {
	fontSize: "11px",
	lineHeight: "16px",
	color: mail.muted,
	fontWeight: 600,
}

const h1: CSSProperties = {
	fontSize: "24px",
	lineHeight: "28px",
	fontWeight: 600,
	color: mail.heading,
	margin: 0,
}

const eyebrow: CSSProperties = {
	fontSize: "12px",
	lineHeight: "18px",
	letterSpacing: "0.04em",
	textTransform: "uppercase",
	color: mail.muted,
	fontWeight: 600,
	margin: "0 0 8px 0",
}

const pillButton: CSSProperties = {
	backgroundColor: "#0A0A0A",
	color: "#FFFFFF",
	fontSize: "14px",
	fontWeight: 500,
	lineHeight: "20px",
	padding: "12px 22px",
	borderRadius: "9999px",
	display: "inline-block",
	letterSpacing: "-0.005em",
}

const ghostButton: CSSProperties = {
	border: `1px solid ${mail.border}`,
	color: mail.heading,
	backgroundColor: "#FFFFFF",
	fontSize: "13px",
	fontWeight: 500,
	lineHeight: "18px",
	padding: "8px 14px",
	borderRadius: "9999px",
	display: "inline-block",
}

const linkStyle: CSSProperties = {
	color: mail.heading,
	fontSize: "13px",
	fontWeight: 500,
	borderBottom: `1px solid ${mail.border}`,
}

interface Item {
	title: string
	blurb: string
	upvotes: number
	identifier: string
}

const ITEMS: Item[] = [
	{
		title: "Slack Connect channels in the inbox",
		blurb:
			"Every shared channel lands as a thread, with replies going back to Slack.",
		upvotes: 24,
		identifier: "PUB-47",
	},
	{
		title: "Bulk actions on the thread list",
		blurb: "Select a range, then assign, tag, or close it in one keystroke.",
		upvotes: 11,
		identifier: "PUB-45",
	},
	{
		title: "Saved views for the inbox",
		blurb: "Pin a filter set to the sidebar and share it with your team.",
		upvotes: 8,
		identifier: "PUB-44",
	},
	{
		title: "CSV export for contacts",
		blurb: "Pull the full contact list with company, tier, and last reply.",
		upvotes: 5,
		identifier: "PUB-41",
	},
	{
		title: "Keyboard shortcuts for triage",
		blurb: "Move through the queue without reaching for the mouse.",
		upvotes: 3,
		identifier: "PUB-39",
	},
]

const SCALE = ["Critical", "Useful", "Skip it"]

// Outlook drops border-radius, so the mark is a glyph and never a rounded span.
function Dot({ color = mail.accent }: { color?: string }) {
	return (
		<span style={{ color, fontSize: "11px", marginRight: "9px" }}>{"●"}</span>
	)
}

function Checkbox({ round }: { round?: boolean }) {
	// The radio is a glyph, since Outlook drops border-radius and squares it off.
	if (round) {
		return (
			<span style={{ color: "#C9C8CE", fontSize: "18px", marginRight: "10px" }}>
				{"\u25CB"}
			</span>
		)
	}
	return (
		<span
			style={{
				display: "inline-block",
				width: "14px",
				height: "14px",
				border: "1.5px solid #C9C8CE",
				borderRadius: "4px",
				marginRight: "11px",
			}}
		/>
	)
}

function ItemCard({
	children,
	style,
}: {
	children: ReactNode
	style?: CSSProperties
}) {
	return (
		<div
			style={{
				border: `1px solid ${mail.border}`,
				borderRadius: "14px",
				padding: "2px 20px",
				margin: "22px 0",
				...style,
			}}
		>
			{children}
		</div>
	)
}

function ItemRow({
	first,
	leading,
	title,
	blurb,
	trailing,
	children,
}: {
	first?: boolean
	leading?: ReactNode
	title: string
	blurb?: string
	trailing?: ReactNode
	children?: ReactNode
}) {
	return (
		<div
			style={{
				borderTop: first ? undefined : `1px solid ${mail.border}`,
				padding: "16px 0",
			}}
		>
			<table
				style={{ width: "100%", borderCollapse: "collapse" }}
				cellPadding={0}
				cellSpacing={0}
			>
				<tbody>
					<tr>
						<td style={{ verticalAlign: "top" }}>
							<p
								style={{
									...secondaryText,
									fontWeight: 600,
									color: mail.heading,
									marginBottom: blurb ? "3px" : 0,
								}}
							>
								{leading ?? <Dot />}
								{title}
							</p>
							{blurb && (
								<p style={{ ...secondaryText, marginLeft: "20px" }}>{blurb}</p>
							)}
							{children && (
								<div style={{ marginTop: "12px", marginLeft: "20px" }}>
									{children}
								</div>
							)}
						</td>
						{trailing && (
							<td
								style={{
									verticalAlign: "middle",
									textAlign: "right",
									whiteSpace: "nowrap",
									width: "110px",
								}}
							>
								{trailing}
							</td>
						)}
					</tr>
				</tbody>
			</table>
		</div>
	)
}

function ScaleButtons() {
	return (
		<>
			{SCALE.map((label, index) => (
				<span
					key={label}
					style={{
						...ghostButton,
						marginRight: index === SCALE.length - 1 ? 0 : "8px",
					}}
				>
					{label}
				</span>
			))}
		</>
	)
}

function EmailFrame({
	subject,
	children,
}: {
	subject: string
	children: ReactNode
}) {
	return (
		<div style={{ width: mail.width, fontFamily: mail.font }}>
			<div
				style={{ ...secondaryText, marginBottom: "10px", color: mail.muted }}
			>
				<span style={{ fontWeight: 600, color: mail.secondary }}>Subject</span>
				{"  "}
				{subject}
			</div>
			<div
				style={{
					backgroundColor: mail.canvas,
					padding: "32px 16px",
					borderRadius: "12px",
				}}
			>
				<div style={{ maxWidth: mail.width, margin: "0 auto" }}>
					<div
						style={{
							backgroundColor: mail.card,
							border: `1px solid ${mail.border}`,
							borderRadius: mail.radius,
							padding: `${mail.padding}px`,
							boxShadow:
								"0 1px 2px rgba(15, 15, 15, 0.04), 0 8px 24px rgba(15, 15, 15, 0.04)",
						}}
					>
						<div
							style={{
								width: "40px",
								height: "40px",
								borderRadius: "8px",
								backgroundColor: "#0A0A0A",
								color: "#FFFFFF",
								fontSize: "18px",
								fontWeight: 600,
								lineHeight: "40px",
								textAlign: "center",
								marginBottom: "40px",
							}}
						>
							A
						</div>
						{children}
					</div>
					<div style={{ padding: "24px 32px 0" }}>
						<span style={{ ...legalText, marginRight: "16px" }}>
							Powered by Productlane
						</span>
						<span style={legalText}>Unsubscribe</span>
					</div>
				</div>
			</div>
		</div>
	)
}

function Intro({ children }: { children: ReactNode }) {
	return <p style={{ ...bodyText, marginTop: "18px" }}>{children}</p>
}

function ViewAllFooter({ note }: { note?: string }) {
	return (
		<div style={{ paddingTop: "4px" }}>
			<div style={{ marginBottom: note ? "10px" : 0 }}>
				<span style={pillButton}>View all</span>
			</div>
			{note && <p style={secondaryText}>{note}</p>}
		</div>
	)
}

// 1. One row per item, the shape that survives a week with twelve new items.
export function DigestList() {
	return (
		<EmailFrame subject="5 new on the Acme roadmap">
			<div style={h1}>Five new on the roadmap</div>
			<Intro>Week of 15 September.</Intro>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
						trailing={<span style={linkStyle}>Give feedback</span>}
					/>
				))}
			</ItemCard>
			<ViewAllFooter />
		</EmailFrame>
	)
}

// 2. One item carries the email; the rest ride along so nothing goes unseen.
export function DigestHero() {
	const [hero, ...rest] = ITEMS
	if (!hero) return null
	return (
		<EmailFrame subject="Slack Connect channels in the inbox, and 4 more">
			<div style={h1}>{hero.title}</div>
			<Intro>{hero.blurb}</Intro>
			<Intro>
				You asked for this one in April. We are shaping it now, so this is the
				week your notes change the design.
			</Intro>
			<p
				style={{
					...bodyText,
					marginTop: "20px",
					fontWeight: 600,
					color: mail.heading,
				}}
			>
				What does your team do in the shared channel today?
			</p>
			<div style={{ marginTop: "20px" }}>
				<span style={pillButton}>Leave your feedback</span>
			</div>
			<p style={{ ...eyebrow, marginTop: "30px" }}>Also new this week</p>
			<ItemCard style={{ marginTop: "0" }}>
				{rest.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
					/>
				))}
			</ItemCard>
		</EmailFrame>
	)
}

// 3. Each item asks its own question, with the two answers as buttons.
export function DigestPoll() {
	return (
		<EmailFrame subject="Five ideas on the roadmap. Which one matters to you?">
			<div style={h1}>Five ideas we are weighing</div>
			<Intro>
				Each of these came from a customer request. Say which ones you want and
				we will know what to build first.
			</Intro>
			<ItemCard>
				{ITEMS.slice(0, 3).map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
					>
						<span style={{ ...ghostButton, marginRight: "8px" }}>
							I want this
						</span>
						<span style={ghostButton}>Tell them why</span>
					</ItemRow>
				))}
			</ItemCard>
			<ViewAllFooter note="Two more are waiting on the board." />
		</EmailFrame>
	)
}

// 3a. A checklist. One tick per item, one button at the end, nothing else.
export function SurveyPick() {
	return (
		<EmailFrame subject="Marie, which of these five should Acme build?">
			<div style={h1}>Help shape what we build next</div>
			<Intro>
				Your feedback shapes what Acme builds. Five new items landed on the
				roadmap this week. Tick the ones your team would use, and we put those
				first.
			</Intro>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						leading={<Checkbox />}
						title={item.title}
						blurb={item.blurb}
					/>
				))}
			</ItemCard>
			<ViewAllFooter note="Takes about twenty seconds." />
		</EmailFrame>
	)
}

// 3b. A three point scale, the shape people recognise from a real survey.
export function SurveyScale() {
	return (
		<EmailFrame subject="Help shape what Acme builds next">
			<div style={h1}>Help shape what we build next</div>
			<Intro>
				Your feedback shapes what Acme builds. Five new items landed on the
				roadmap this week. Rate each one in a tap. We build in the order our
				customers set.
			</Intro>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
					>
						<ScaleButtons />
					</ItemRow>
				))}
			</ItemCard>
			<ViewAllFooter note="Two more are waiting on the board." />
		</EmailFrame>
	)
}

// 3c. A single question, five answers, and an open box for what we missed.
export function SurveyOneClick() {
	return (
		<EmailFrame subject="One question: what should Acme build first?">
			<div style={{ ...h1, fontSize: "21px", lineHeight: "27px" }}>
				Five items are new this week. Which one do you want first?
			</div>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						leading={<Checkbox round />}
						title={item.title}
					/>
				))}
			</ItemCard>
			<p
				style={{
					...bodyText,
					fontWeight: 600,
					color: mail.heading,
					marginBottom: "10px",
				}}
			>
				Something else on your mind?
			</p>
			<div
				style={{
					border: `1px solid ${mail.border}`,
					borderRadius: "12px",
					padding: "14px 16px",
					fontSize: "14px",
					lineHeight: "21px",
					color: mail.muted,
					backgroundColor: "#FBFBFC",
					marginBottom: "20px",
				}}
			>
				Tell us what your team needs from Acme
			</div>
			<ViewAllFooter />
		</EmailFrame>
	)
}

// 6. Opens with what their last votes turned into, so the ask is already paid for.
export function SurveyImpact() {
	const shipped: [string, string][] = [
		["Keyboard shortcuts for triage", "You rated this Critical in March"],
		["Quieter thread list", "You rated this Useful in July"],
	]
	return (
		<EmailFrame subject="Two things you rated shipped. Five more to rate.">
			<div style={h1}>Help shape what we build next</div>
			<Intro>
				Two of the things you rated last quarter are live. Five more landed on
				the roadmap this week. Rate each one in a tap.
			</Intro>
			<ItemCard>
				{shipped.map(([title, note], index) => (
					<ItemRow key={title} first={index === 0} title={title} blurb={note} />
				))}
			</ItemCard>
			<ItemCard>
				{ITEMS.slice(0, 3).map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
					>
						<ScaleButtons />
					</ItemRow>
				))}
			</ItemCard>
			<ViewAllFooter note="Two more are waiting on the board." />
		</EmailFrame>
	)
}

// 7. The item that traces back to this person's own report comes first, and says so.
export function SurveyFromYourReport() {
	const [mine, ...rest] = ITEMS
	if (!mine) return null
	return (
		<EmailFrame subject="Your request is on the Acme roadmap">
			<div style={h1}>Help shape what we build next</div>
			<Intro>
				One of this week&apos;s new roadmap items came from what you wrote to us
				in March. Rate it and the ones below it.
			</Intro>
			<p style={{ ...eyebrow, marginTop: "26px" }}>From your report</p>
			<ItemCard style={{ marginTop: 0, backgroundColor: "#FBFBFC" }}>
				<ItemRow first title={mine.title} blurb={mine.blurb}>
					<ScaleButtons />
				</ItemRow>
			</ItemCard>
			<ItemCard>
				{rest.slice(0, 2).map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
					>
						<ScaleButtons />
					</ItemRow>
				))}
			</ItemCard>
			<ViewAllFooter note="Two more are waiting on the board." />
		</EmailFrame>
	)
}

// 8. A fixed budget turns rating into spending, so the answer carries a cost.
export function SurveyVoteBudget() {
	return (
		<EmailFrame subject="You have three votes this week">
			<div style={h1}>Help shape what we build next</div>
			<Intro>
				Five new items landed on the Acme roadmap this week. You have three
				votes to spend on them. Whatever you back moves up the queue.
			</Intro>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
						blurb={item.blurb}
						trailing={
							<span style={ghostButton}>
								{index === 0 ? "Voted" : "Use a vote"}
							</span>
						}
					/>
				))}
			</ItemCard>
			<ViewAllFooter note="Unspent votes roll into next week." />
		</EmailFrame>
	)
}

// 4. The email mirrors the board, so the portal is familiar on arrival.
export function DigestBoard() {
	const groups: [string, Item[]][] = [
		["In progress", ITEMS.slice(0, 2)],
		["Under consideration", ITEMS.slice(2)],
	]
	return (
		<EmailFrame subject="This week on the Acme roadmap">
			<div style={h1}>This week on the roadmap</div>
			<Intro>
				Five items moved onto the board. Add a comment on any of them and it
				reaches the team building it.
			</Intro>
			{groups.map(([group, items]) => (
				<div key={group} style={{ marginTop: "26px" }}>
					<p style={eyebrow}>{group}</p>
					<ItemCard style={{ marginTop: 0, marginBottom: 0 }}>
						{items.map((item, index) => (
							<ItemRow
								key={item.identifier}
								first={index === 0}
								title={item.title}
								blurb={item.identifier}
								trailing={
									<span style={{ ...secondaryText, color: mail.secondary }}>
										{"▲"} {item.upvotes}
									</span>
								}
							/>
						))}
					</ItemCard>
				</div>
			))}
			<div style={{ marginTop: "26px" }}>
				<ViewAllFooter />
			</div>
		</EmailFrame>
	)
}

// 5. A person writes it. Highest reply rate, and the cheapest to build.
export function DigestNote() {
	return (
		<EmailFrame subject="Five things we added to the roadmap">
			<p style={bodyText}>Hi there,</p>
			<Intro>
				Five items went onto the Acme roadmap this week. Two of them started
				from something a customer wrote to us.
			</Intro>
			<ItemCard>
				{ITEMS.map((item, index) => (
					<ItemRow
						key={item.identifier}
						first={index === 0}
						title={item.title}
					/>
				))}
			</ItemCard>
			<p style={bodyText}>
				Bulk actions is the one we are least sure about. If you select a run of
				threads today, what do you do with them next?
			</p>
			<p style={{ ...bodyText, marginTop: "16px" }}>
				Reply here, or leave a comment on the roadmap.
			</p>
			<p style={{ ...bodyText, marginTop: "24px" }}>
				Jonas
				<br />
				<span style={{ color: mail.secondary }}>Acme</span>
			</p>
		</EmailFrame>
	)
}

const SIDEBAR_ROWS = [
	"Roadmap",
	"Under consideration",
	"Request issue",
	"Changelog",
]

function SidebarSubscribeCard({ compact }: { compact?: boolean }) {
	if (compact) {
		return (
			<div className="px-3 pb-3">
				<div className="pb-2 text-xs font-medium text-primary">
					Get the weekly roadmap email
				</div>
				<div className="flex gap-1.5">
					<input
						readOnly
						placeholder="your@email.com"
						className="h-7 min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-500 dark:border-gray-700/80 dark:bg-gray-850 dark:text-gray-400"
					/>
					<button
						type="button"
						className="h-7 flex-none rounded-md bg-gray-900 px-2.5 text-xs font-medium text-white dark:bg-white dark:text-gray-900"
					>
						Join
					</button>
				</div>
			</div>
		)
	}
	return (
		<div className="px-3 pb-3">
			<div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700/80 dark:bg-gray-850">
				<div className="pb-2 text-xs font-medium text-primary">
					Get the weekly roadmap email
				</div>
				<input
					readOnly
					placeholder="your@email.com"
					className="mb-2 h-8 w-full rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-500 dark:border-gray-700/80 dark:bg-gray-900 dark:text-gray-400"
				/>
				<button
					type="button"
					className="h-8 w-full rounded-md bg-gray-900 text-xs font-medium text-white dark:bg-white dark:text-gray-900"
				>
					Subscribe
				</button>
			</div>
		</div>
	)
}

function SidebarPreview({ children }: { children: ReactNode }) {
	return (
		<div className="flex h-[400px] w-[260px] flex-none flex-col overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-700/80 dark:bg-gray-900">
			<div className="px-4 pb-3 pt-4 text-sm font-semibold text-primary">
				Acme Roadmap
			</div>
			<div className="flex-1 overflow-hidden px-3">
				{SIDEBAR_ROWS.map((row) => (
					<div
						key={row}
						className="rounded-md px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400"
					>
						{row}
					</div>
				))}
			</div>
			{children}
		</div>
	)
}

export function PortalDigestEmailPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1320px] px-8 py-10 pb-24">
				<div className="mb-10 max-w-[640px]">
					<div className="text-lg font-semibold text-primary">
						Weekly roadmap email
					</div>
					<div className="mt-1 text-sm text-secondary">
						Goes to contacts who upvoted or wrote feedback on the portal. Every
						design caps at five items, carries the same unsubscribe link as the
						changelog broadcast, and lists items in one bordered card with a
						green mark per row.
					</div>
				</div>

				<div className="flex flex-wrap gap-x-12 gap-y-14">
					<Variant
						label="3b. Three point scale (shipping)"
						note="Critical, useful, skip it. The shape people know from a real survey, so nobody has to work out what the buttons mean. This is the one in the code."
					>
						<SurveyScale />
					</Variant>
					<Variant
						label="1. The list"
						note="One row per item with a feedback link on each. Reads the same with five items or twelve, and it is closest to the changelog email people already get."
					>
						<DigestList />
					</Variant>
					<Variant
						label="2. Lead item, then the rest"
						note="One item gets the headline and the question. The others ride along as one line each. Best when a single project deserves the attention."
					>
						<DigestHero />
					</Variant>
					<Variant
						label="3. One question per card"
						note="Two buttons per item. I want this records the upvote from the email; Tell them why opens the comment box on the portal."
					>
						<DigestPoll />
					</Variant>
					<Variant
						label="3a. Pick what matters"
						note="A checklist. One tick per item, one button at the end. The lowest thing you can ask of a person, and it returns a ranked list by Friday."
					>
						<SurveyPick />
					</Variant>
					<Variant
						label="3c. One question, one click"
						note="A single question with five answers, then an open box for what we missed. Highest completion, and the text box is where the quotes come from."
					>
						<SurveyOneClick />
					</Variant>
					<Variant
						label="4. The board"
						note="Groups by portal column and carries the identifier and the upvote count. The reader arrives on a page that looks like what they just read."
					>
						<DigestBoard />
					</Variant>
					<Variant
						label="5. A note from the team"
						note="Plain type, signed, one real question at the end. The card gives the list structure the original version lacked, at the cost of some of its hand-written feel."
					>
						<DigestNote />
					</Variant>
					<Variant
						label="6. Impact first"
						note="Opens with the items this person rated that have since shipped. The strongest argument a survey can make, and the one the data cannot support today."
					>
						<SurveyImpact />
					</Variant>
					<Variant
						label="7. From your report"
						note="The item that traces back to their own words goes first, in a box that says so. Rarer still than 6, and it needs the CustomerNeed link to be right."
					>
						<SurveyFromYourReport />
					</Variant>
					<Variant
						label="8. Three votes"
						note="A fixed budget instead of a rating. Spending a vote costs something, so the ranking you get back means more than a row of taps."
					>
						<SurveyVoteBudget />
					</Variant>
				</div>

				<div className="mb-6 mt-20 max-w-[640px]">
					<div className="text-lg font-semibold text-primary">
						Sidebar subscribe form
					</div>
					<div className="mt-1 text-sm text-secondary">
						Bottom left of the portal sidebar, under the nav. Submitting creates
						a contact with isSubscribed true, which is the same row the
						unsubscribe link clears.
					</div>
				</div>
				<div className="flex flex-wrap gap-8">
					<Variant label="Card" note="Boxed, reads as its own surface.">
						<SidebarPreview>
							<SidebarSubscribeCard />
						</SidebarPreview>
					</Variant>
					<Variant
						label="Inline"
						note="No box, sits in the column like the nav rows above it."
					>
						<SidebarPreview>
							<SidebarSubscribeCard compact />
						</SidebarPreview>
					</Variant>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="max-w-[584px] pb-2 text-xxs text-tertiary">{note}</div>
			{children}
		</div>
	)
}
